// components/common/AtcArea/AtcAreaProvider.tsx
"use client";

import React, { ReactNode, useMemo, useState } from "react";

import { AtcAreaContext, AtcAreaContextType } from "@/context/AtcAreaContext";
import type { AtcProduct } from "@/types/atc";

interface AtcAreaProviderProps {
  children: ReactNode;
}

export function AtcAreaProvider({ children }: AtcAreaProviderProps) {
  // Manage all state variables using useState
  const [atcProducts, setAtcProducts] = useState<AtcProduct[]>([]);
  const [selectedProductId, setSelectedProductId] = useState<string>("");
  const [selectedOptionId, setSelectedOptionId] = useState<number>(0);
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [purchaseSource, setPurchaseSource] = useState<string>("");
  const [promoCode, setPromoCode] = useState<string | undefined>(undefined);
  const [showWelcomeKit, setShowWelcomeKit] = useState<boolean>(false);
  const [oneTimePurchaseSource, setOneTimePurchaseSource] = useState<string>("");

  // Use useMemo to derive the selectedProduct object from the state
  const selectedProduct = useMemo(() => {
    return atcProducts.find((p) => p.id === selectedProductId) || null;
  }, [atcProducts, selectedProductId]);

  // Combine all state variables and setters into the context value
  const contextValue = useMemo(() => {
    return {
      atcProducts,
      setAtcProducts,
      selectedProduct,
      selectedOptionId,
      setSelectedProductId,
      setSelectedOptionId,
      modalOpen,
      setModalOpen,
      purchaseSource,
      setPurchaseSource,
      promoCode,
      setPromoCode,
      showWelcomeKit,
      setShowWelcomeKit,
      oneTimePurchaseSource,
      setOneTimePurchaseSource,
    } as AtcAreaContextType;
  }, [
    atcProducts,
    selectedProduct,
    selectedOptionId,
    modalOpen,
    purchaseSource,
    promoCode,
    showWelcomeKit,
    oneTimePurchaseSource,
  ]);

  return <AtcAreaContext.Provider value={contextValue}>{children}</AtcAreaContext.Provider>;
}
